import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Stack,
  Grid,
  InputAdornment,
  useTheme,
  Avatar,
  alpha,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search as SearchIcon,
  Router as RouterIcon,
  CheckCircle as CheckIcon,
  Block as BlockIcon,
  Cancel as CancelIcon,
  HourglassEmpty as PendingIcon,
  Build as BuildIcon,
  LockOpen as LockOpenIcon,
  CancelOutlined as CancelOutlinedIcon,
  Wifi as WifiIcon,
  WifiOff as WifiOffIcon,
  Phone as PhoneIcon,
  Tv as TvIcon,
  Layers as LayersIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import ContratoServicosService, {
  ContratoServicos,
  GetAllServicosParams,
  UpdateStatusData,
} from '../services/contratoServicosService';
import ServicosTable from '../components/contract/ServicosTable';
import SysDateRangePicker from '../components/SysDateRangePicker';
import { usePermissions } from '../contexts/AuthContext';
import { detailPageHeaderPaperSx } from '../styles/theme';

const ServicesPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { can } = usePermissions();
  const podeEditar = can('contratos.editar');
  const isDark = theme.palette.mode === 'dark';

  // Estados de Paginação
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);

  // Estados de Filtros Aplicados
  const [activeStatusFilter, setActiveStatusFilter] = useState<string>('all');
  const [activeTipoFilter, setActiveTipoFilter] = useState<string>('all');
  const [activeConexaoFilter, setActiveConexaoFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Estados Temporários do Formulário de Filtro
  const [tempSearch, setTempSearch] = useState('');
  const [tempStatus, setTempStatus] = useState<string>('all');
  const [tempTipo, setTempTipo] = useState<string>('all');
  const [tempConexao, setTempConexao] = useState<string>('all');
  const [tempStartDate, setTempStartDate] = useState('');
  const [tempEndDate, setTempEndDate] = useState('');

  // Estados para Ações em Massa e Reparação
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [isBulkRepairing, setIsBulkRepairing] = useState(false);
  const [isBulkLiberandoMac, setIsBulkLiberandoMac] = useState(false);

  const queryClient = useQueryClient();

  // ─── Queries ───────────────────────────────────────────────────────────────

  const queryParams = useMemo<GetAllServicosParams>(() => {
    const params: GetAllServicosParams = {
      page: page + 1,
      limit: rowsPerPage,
    };
    if (search.trim()) params.search = search.trim();
    if (activeStatusFilter !== 'all') params.status = activeStatusFilter;
    if (activeTipoFilter !== 'all') params.tipo = activeTipoFilter;
    if (activeTipoFilter === 'internet' && activeConexaoFilter !== 'all') {
      params.conexao = activeConexaoFilter;
    }
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    return params;
  }, [page, rowsPerPage, search, activeStatusFilter, activeTipoFilter, activeConexaoFilter, startDate, endDate]);

  const {
    data: servicosResponse,
    isLoading: loadingServicos,
    isFetching,
  } = useQuery({
    queryKey: ['services-list', queryParams],
    queryFn: () => ContratoServicosService.getAll(queryParams),
    staleTime: 30_000,
  });

  const {
    data: statsResponse,
    isLoading: loadingStats,
  } = useQuery({
    queryKey: ['services-stats'],
    queryFn: () => ContratoServicosService.getStats(),
    staleTime: 60_000,
  });

  const servicos = servicosResponse?.data || [];
  const totalRegistros = servicosResponse?.pagination?.total || 0;
  const stats = statsResponse?.data;

  // ─── Handlers de Filtros ─────────────────────────────────────────────────────

  const handleApplyFilters = () => {
    setSearch(tempSearch);
    setActiveStatusFilter(tempStatus);
    setActiveTipoFilter(tempTipo);
    setActiveConexaoFilter(tempTipo === 'internet' ? tempConexao : 'all');
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
    setPage(0);
    toast.success('Filtros aplicados');
  };

  const handleClearFilters = () => {
    setTempSearch('');
    setTempStatus('all');
    setTempTipo('all');
    setTempConexao('all');
    setTempStartDate('');
    setTempEndDate('');

    setSearch('');
    setActiveStatusFilter('all');
    setActiveTipoFilter('all');
    setActiveConexaoFilter('all');
    setStartDate('');
    setEndDate('');
    setPage(0);
    toast.info('Filtros limpos');
  };

  const handleCardFilter = (statusKey: string, tipoKey: string = 'all', conexaoKey: string = 'all') => {
    setActiveStatusFilter(statusKey);
    setActiveTipoFilter(tipoKey);
    setActiveConexaoFilter(conexaoKey);
    setTempStatus(statusKey);
    setTempTipo(tipoKey);
    setTempConexao(conexaoKey);
    setPage(0);
  };

  // ─── Handlers de Ações ─────────────────────────────────────────────────────

  const handleCancelServico = async (servico: ContratoServicos) => {
    const result = await Swal.fire({
      title: 'Cancelar Serviço',
      text: `Tem certeza que deseja cancelar o serviço ${servico.tipo} #${servico.id}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sim, cancelar',
      cancelButtonText: 'Não',
      confirmButtonColor: '#f57c00',
      input: 'textarea',
      inputLabel: 'Motivo do cancelamento (opcional)',
      inputPlaceholder: 'Digite o motivo do cancelamento...',
    });

    if (result.isConfirmed) {
      try {
        const motivo = result.value || 'Cancelamento manual';
        const response = await ContratoServicosService.updateStatus(servico.id, {
          status: 'cancelado',
          observacoes: motivo,
        });

        if (response.success) {
          toast.success('Serviço cancelado com sucesso!');
          queryClient.invalidateQueries({ queryKey: ['services-list'] });
          queryClient.invalidateQueries({ queryKey: ['services-stats'] });
        } else {
          toast.error(response.message || 'Erro ao cancelar serviço');
        }
      } catch (err: any) {
        toast.error('Erro ao cancelar serviço: ' + err.message);
      }
    }
  };

  const handleRepairConnection = async (servico: ContratoServicos) => {
    const confirmResult = await Swal.fire({
      title: 'Reparar Conexão',
      text: `Deseja recriar as credenciais e sincronizar o serviço PPPoE #${servico.id} (${servico.usuario_pppoe}) no RADIUS/MikroTik?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sim, Reparar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: theme.palette.primary.main,
      cancelButtonColor: theme.palette.error.main,
    });

    if (!confirmResult.isConfirmed) return;

    Swal.fire({
      title: 'Reparando Conexão...',
      text: 'Aguarde enquanto a conexão é sincronizada.',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      const res = await ContratoServicosService.repararConexao(servico.id);
      if (res.success) {
        Swal.fire({
          title: 'Conexão Reparada!',
          text: res.message || 'Sincronização concluída com sucesso.',
          icon: 'success',
          confirmButtonColor: theme.palette.primary.main,
        });
        queryClient.invalidateQueries({ queryKey: ['services-list'] });
      } else {
        Swal.fire({
          title: 'Atenção',
          text: res.message || 'Ocorreu um alerta ao reparar conexão.',
          icon: 'warning',
          confirmButtonColor: theme.palette.primary.main,
        });
      }
    } catch (error: any) {
      Swal.fire({
        title: 'Erro!',
        text: error.message || 'Erro ao reparar conexão.',
        icon: 'error',
        confirmButtonColor: theme.palette.primary.main,
      });
    }
  };

  const handleLiberarMac = async (servico: ContratoServicos) => {
    const confirmResult = await Swal.fire({
      title: 'Liberar MAC Address',
      text: `Deseja remover o endereço MAC vinculado ao serviço #${servico.id} (${servico.usuario_pppoe}) e desconectar a sessão ativa?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, Liberar MAC',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: theme.palette.warning.main,
      cancelButtonColor: theme.palette.text.secondary,
    });

    if (!confirmResult.isConfirmed) return;

    try {
      const res = await ContratoServicosService.liberarMac(servico.id);
      toast.success(res.message || 'Endereço MAC liberado com sucesso');
      queryClient.invalidateQueries({ queryKey: ['services-list'] });
    } catch (error: any) {
      toast.error(error.message || 'Erro ao liberar MAC');
    }
  };

  const handleBulkLiberarMac = async () => {
    if (selectedIds.length === 0) return;

    const confirmResult = await Swal.fire({
      title: 'Liberar MAC em Massa',
      text: `Deseja realmente liberar o endereço MAC de ${selectedIds.length} serviço(s)? As sessões ativas serão desconectadas.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, Liberar Todos',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: theme.palette.warning.main,
    });

    if (!confirmResult.isConfirmed) return;

    setIsBulkLiberandoMac(true);
    let processados = 0;

    Swal.fire({
      title: 'Liberando MACs...',
      html: `Processando <b>0</b> de ${selectedIds.length} serviço(s)...`,
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      for (const id of selectedIds) {
        try {
          await ContratoServicosService.liberarMac(id);
        } catch (err) {
          console.error(`Erro ao liberar MAC do serviço ${id}:`, err);
        }
        processados++;
        Swal.update({
          html: `Processando <b>${processados}</b> de ${selectedIds.length} serviço(s)...`,
        });
      }

      Swal.fire({
        title: 'Concluído!',
        text: `Liberação de MAC finalizada para ${processados} serviço(s).`,
        icon: 'success',
        confirmButtonColor: theme.palette.primary.main,
      });
    } catch (error) {
      console.error('Erro ao liberar MAC em massa:', error);
      Swal.fire({
        title: 'Erro!',
        text: 'Ocorreu um erro durante a liberação de MAC em massa.',
        icon: 'error',
      });
    } finally {
      setIsBulkLiberandoMac(false);
      setSelectedIds([]);
      queryClient.invalidateQueries({ queryKey: ['services-list'] });
    }
  };

  const handleBulkRepairConnection = async () => {
    if (selectedIds.length === 0) return;

    const confirmResult = await Swal.fire({
      title: 'Reparar Conexão em Massa',
      text: `Deseja realmente reparar a conexão de ${selectedIds.length} serviço(s)?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sim, Reparar Todos',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: theme.palette.primary.main,
    });

    if (!confirmResult.isConfirmed) return;

    setIsBulkRepairing(true);
    let processados = 0;

    Swal.fire({
      title: 'Reparando Conexões...',
      html: `Processando <b>0</b> de ${selectedIds.length} serviço(s)...`,
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      for (const id of selectedIds) {
        try {
          await ContratoServicosService.repararConexao(id);
        } catch (err) {
          console.error(`Erro ao reparar serviço ${id}:`, err);
        }
        processados++;
        Swal.update({
          html: `Processando <b>${processados}</b> de ${selectedIds.length} serviço(s)...`,
        });
      }

      Swal.fire({
        title: 'Concluído!',
        text: `Processamento finalizado para ${processados} serviço(s).`,
        icon: 'success',
        confirmButtonColor: theme.palette.primary.main,
      });
    } catch (error) {
      console.error('Erro na reparação em massa:', error);
      Swal.fire({
        title: 'Erro!',
        text: 'Ocorreu um erro durante a reparação em massa.',
        icon: 'error',
      });
    } finally {
      setIsBulkRepairing(false);
      setSelectedIds([]);
      queryClient.invalidateQueries({ queryKey: ['services-list'] });
    }
  };

  // ─── Renderização de KPI Cards ──────────────────────────────────────────────

  const renderKpiCard = (
    titulo: string,
    valor: number | string,
    icone: React.ReactElement,
    cor: string,
    statusKey: string,
    tipoKey: string = 'all',
    conexaoKey: string = 'all',
    sub?: string,
    isGradient = false
  ) => {
    const isActive = activeStatusFilter === statusKey && activeTipoFilter === tipoKey && activeConexaoFilter === conexaoKey;

    if (isGradient) {
      return (
        <Paper
          elevation={isActive ? 4 : 1}
          onClick={() => handleCardFilter(statusKey, tipoKey, conexaoKey)}
          sx={{
            p: 2,
            borderRadius: 3,
            background: isActive
              ? `linear-gradient(135deg, ${cor} 0%, ${alpha(cor, 0.75)} 100%)`
              : `linear-gradient(135deg, ${alpha(cor, 0.9)} 0%, ${alpha(cor, 0.7)} 100%)`,
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'pointer',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            transition: 'all 0.2s',
            transform: isActive ? 'scale(1.02)' : 'none',
            '&:hover': { transform: 'translateY(-3px) scale(1.01)', boxShadow: 6 },
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography variant="caption" sx={{ opacity: 0.9, fontWeight: 700, textTransform: 'uppercase', fontSize: '0.65rem' }}>
              {titulo}
            </Typography>
            <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: -0.5 }}>
              {loadingStats ? '...' : valor}
            </Typography>
            {sub && (
              <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 600, fontSize: '0.65rem' }}>
                {sub}
              </Typography>
            )}
          </Box>
          {React.cloneElement(icone as any, {
            sx: {
              position: 'absolute',
              right: -10,
              bottom: -10,
              fontSize: 80,
              opacity: 0.15,
              transform: 'rotate(-15deg)',
            },
          })}
        </Paper>
      );
    }

    return (
      <Paper
        elevation={isActive ? 4 : 1}
        onClick={() => handleCardFilter(statusKey, tipoKey, conexaoKey)}
        sx={{
          p: 2,
          borderRadius: 3,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s',
          border: isActive ? `2px solid ${cor}` : `1px solid ${alpha(cor, isDark ? 0.28 : 0.15)}`,
          bgcolor: isActive ? alpha(cor, isDark ? 0.12 : 0.04) : 'background.paper',
          '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: isDark ? `0 8px 24px ${alpha(cor, 0.15)}` : theme.shadows[4],
            bgcolor: alpha(cor, isDark ? 0.1 : 0.04),
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
          <Avatar
            sx={{
              bgcolor: alpha(cor, 0.1),
              color: cor,
              width: 44,
              height: 44,
              flexShrink: 0,
            }}
          >
            {icone}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', fontSize: '0.65rem' }}>
              {titulo}
            </Typography>
            <Typography variant="h5" fontWeight={800} color="text.primary">
              {loadingStats ? '...' : valor}
            </Typography>
            {sub && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block', mt: -0.2 }}>
                {sub}
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>
    );
  };

  const cores = {
    primario: theme.palette.primary.main,
    ativo: theme.palette.success.main,
    bloqueado: theme.palette.warning.main,
    cancelado: theme.palette.error.main,
    aguardando: theme.palette.info.main,
    secundario: theme.palette.secondary.main,
  };

  return (
    <Box sx={{ p: 0.5, pb: 2, bgcolor: 'background.default', minHeight: '100%' }}>
      {/* Header */}
      <Paper elevation={0} sx={detailPageHeaderPaperSx(theme)}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.15), color: 'primary.main', width: 40, height: 40 }}>
              <RouterIcon sx={{ fontSize: 22 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={800} sx={{ fontSize: '1.05rem', lineHeight: 1.2 }}>
                Gestão de Serviços
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                Visualização e gerenciamento de todos os serviços do sistema
              </Typography>
            </Box>
          </Box>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end">
            {selectedIds.length > 0 && podeEditar && (
              <>
                <Button
                  variant="outlined"
                  color="warning"
                  size="small"
                  startIcon={isBulkLiberandoMac ? <CircularProgress size={18} color="warning" /> : <LockOpenIcon sx={{ fontSize: 16 }} />}
                  onClick={handleBulkLiberarMac}
                  disabled={isBulkLiberandoMac || isBulkRepairing}
                  sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.78rem', borderRadius: 1.5 }}
                >
                  {isBulkLiberandoMac ? 'Liberando...' : `Liberar MAC em Massa (${selectedIds.length})`}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  startIcon={isBulkRepairing ? <CircularProgress size={18} /> : <BuildIcon sx={{ fontSize: 16 }} />}
                  onClick={handleBulkRepairConnection}
                  disabled={isBulkRepairing || isBulkLiberandoMac}
                  sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.78rem', borderRadius: 1.5 }}
                >
                  {isBulkRepairing ? 'Reparando...' : `Reparar Conexão em Massa (${selectedIds.length})`}
                </Button>
              </>
            )}
          </Stack>
        </Box>
      </Paper>

      {/* Cards de Resumo */}
      <Grid container spacing={1.5} sx={{ mb: 1 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          {renderKpiCard('Total de Serviços', stats?.total || 0, <LayersIcon />, cores.primario, 'all', 'all', 'all', 'Serviços cadastrados', true)}
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          {renderKpiCard('Serviços Ativos', stats?.ativos || 0, <CheckIcon />, cores.ativo, 'ativo', 'all', 'all')}
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          {renderKpiCard('Conexão Online', stats?.online || 0, <WifiIcon />, theme.palette.success.main, 'all', 'internet', 'Online', 'Internet conectada')}
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          {renderKpiCard('Conexão Offline', stats?.offline || 0, <WifiOffIcon />, theme.palette.error.main, 'all', 'internet', 'Offline', 'Internet sem sessão')}
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          {renderKpiCard('Bloqueados', stats?.bloqueados || 0, <BlockIcon />, cores.bloqueado, 'bloqueado', 'all', 'all')}
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          {renderKpiCard('Ag. Instalação', stats?.aguardando_instalacao || 0, <PendingIcon />, cores.aguardando, 'aguardando_instalacao', 'all', 'all')}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {renderKpiCard('Internet', stats?.internet || 0, <WifiIcon />, cores.primario, 'all', 'internet', 'all')}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {renderKpiCard('Telefonia', stats?.telefonia || 0, <PhoneIcon />, cores.secundario, 'all', 'telefonia', 'all')}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {renderKpiCard('TV', stats?.tv || 0, <TvIcon />, theme.palette.info.main, 'all', 'tv', 'all')}
        </Grid>
      </Grid>

      {/* Barra de Filtros */}
      <Paper sx={{ p: 2, mb: 1, borderRadius: 3, border: `1px solid ${theme.palette.divider}`, ...(isDark && { backgroundImage: 'none' }) }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={tempTipo === 'internet' ? 2.5 : 3}>
            <TextField
              fullWidth
              size="small"
              placeholder="PPPoE, IP, MAC, Cliente, Contrato..."
              value={tempSearch}
              onChange={(e) => setTempSearch(e.target.value)}
              onKeyUp={(e) => e.key === 'Enter' && handleApplyFilters()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} md={tempTipo === 'internet' ? 1.8 : 2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={tempStatus}
                label="Status"
                onChange={(e) => setTempStatus(e.target.value)}
              >
                <MenuItem value="all">Todos os Status</MenuItem>
                <MenuItem value="ativo">Ativo</MenuItem>
                <MenuItem value="bloqueado">Bloqueado</MenuItem>
                <MenuItem value="cancelado">Cancelado</MenuItem>
                <MenuItem value="aguardando_instalacao">Ag. Instalação</MenuItem>
                <MenuItem value="aguardando_assinatura">Ag. Assinatura</MenuItem>
                <MenuItem value="suspenso">Suspenso</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={tempTipo === 'internet' ? 1.8 : 2}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo de Serviço</InputLabel>
              <Select
                value={tempTipo}
                label="Tipo de Serviço"
                onChange={(e) => {
                  const val = e.target.value;
                  setTempTipo(val);
                  if (val !== 'internet') setTempConexao('all');
                }}
              >
                <MenuItem value="all">Todos os Tipos</MenuItem>
                <MenuItem value="internet">Internet</MenuItem>
                <MenuItem value="telefonia">Telefonia</MenuItem>
                <MenuItem value="tv">TV</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {tempTipo === 'internet' && (
            <Grid item xs={12} md={1.8}>
              <FormControl fullWidth size="small">
                <InputLabel>Status da Conexão</InputLabel>
                <Select
                  value={tempConexao}
                  label="Status da Conexão"
                  onChange={(e) => setTempConexao(e.target.value)}
                >
                  <MenuItem value="all">Todas</MenuItem>
                  <MenuItem value="Online">Online</MenuItem>
                  <MenuItem value="Offline">Offline</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} md={tempTipo === 'internet' ? 2.3 : 3}>
            <SysDateRangePicker
              label="Período de Cadastro"
              onChange={(range) => {
                setTempStartDate(range.startDate ? range.startDate.toISOString().split('T')[0] : '');
                setTempEndDate(range.endDate ? range.endDate.toISOString().split('T')[0] : '');
              }}
            />
          </Grid>

          <Grid item xs={12} md={tempTipo === 'internet' ? 1.8 : 2}>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button variant="outlined" onClick={handleClearFilters} size="small" fullWidth>
                Limpar
              </Button>
              <Button variant="contained" onClick={handleApplyFilters} size="small" fullWidth>
                Filtrar
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabela de Serviços */}
      <ServicosTable
        servicos={servicos}
        totalRegistros={totalRegistros}
        loading={loadingServicos || isFetching}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={(r) => {
          setRowsPerPage(r);
          setPage(0);
        }}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        onCancelService={handleCancelServico}
        onRepairConnection={handleRepairConnection}
        onLiberarMac={handleLiberarMac}
      />
    </Box>
  );
};

export default ServicesPage;
